import { Dispatch, SetStateAction, useState, useCallback } from 'react';

type SetValue<T> = Dispatch<SetStateAction<T>>;

export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
    const readValue = () => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? (JSON.parse(item) as T) : initialValue;
        }
        catch (error) {
            console.warn(`Error reading localStorage key ${key}:`, error);
            return initialValue;
        }
    };

    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [ storedValue, setStoredValue ] = useState(readValue);

    // Return a wrapped version of useState's setter function that
    // persists the new value to localStorage.
    const setValue: SetValue<T> = useCallback(value => {
        setStoredValue(previousValue => {
            // Allow value to be a function so we have the same API as useState
            const newValue = value instanceof Function ? value(previousValue) : value;
            try {
                // Save to local storage
                window.localStorage.setItem(key, JSON.stringify(newValue));
            }
            catch (error) {
                console.warn(`Error setting localStorage key ${key}:`, error);
            }
            return newValue;
        });
    }, [ setStoredValue, key ]);

    return [ storedValue, setValue ];
}
